'use client'

import { motion, useInView, type Variants } from 'framer-motion'
import { useRef } from 'react'
import { cn } from '@/utils/cn'

interface TextAnimateProps {
  text: string
  className?: string
  animation?: 'blur' | 'slide' | 'fade'
  by?: 'word' | 'character'
  duration?: number
  delay?: number
  staggerChildren?: number
}

const animationVariants: Record<string, Variants> = {
  blur: {
    hidden: {
      filter: 'blur(10px)',
      opacity: 0,
      y: 5,
    },
    visible: {
      filter: 'blur(0px)',
      opacity: 1,
      y: 0,
    },
  },
  slide: {
    hidden: {
      y: 20,
      opacity: 0,
    },
    visible: {
      y: 0,
      opacity: 1,
    },
  },
  fade: {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
    },
  },
}

export function TextAnimate({
  text,
  className,
  animation = 'blur',
  by = 'word',
  duration = 0.5,
  delay = 0,
  staggerChildren = 0.05,
}: TextAnimateProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.5 })

  const variants = animationVariants[animation]
  const elements = by === 'word' ? text.split(' ') : text.split('')

  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren,
        delayChildren: delay,
      },
    },
  }

  const childVariants: Variants = {
    hidden: variants.hidden,
    visible: {
      ...variants.visible,
      transition: {
        duration,
      },
    },
  }

  return (
    <motion.span
      ref={ref}
      className={cn('inline-block', className)}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      {elements.map((element, index) => (
        <motion.span
          key={`${by}-${index}-${element}`}
          className={cn('inline-block', by === 'word' && 'mr-2 last:mr-0')}
          variants={childVariants}
        >
          {element}
          {by === 'character' && ' '}
        </motion.span>
      ))}
    </motion.span>
  )
}

interface GradientTextProps {
  children: React.ReactNode
  className?: string
  colors?: string[]
  animationSpeed?: number
}

export function GradientText({
  children,
  className,
  colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'],
  animationSpeed = 8,
}: GradientTextProps) {
  const gradientStyle = {
    backgroundImage: `linear-gradient(90deg, ${colors.join(', ')}, ${colors[0]})`,
    backgroundSize: '200% auto',
  }

  return (
    <motion.span
      className={cn('inline-block bg-clip-text text-transparent', className)}
      style={gradientStyle}
      animate={{
        backgroundPosition: ['0% center', '200% center'],
      }}
      transition={{
        duration: animationSpeed,
        repeat: Infinity,
        repeatType: 'loop',
        ease: 'linear',
      }}
    >
      {children}
    </motion.span>
  )
}

interface TypewriterProps {
  text: string
  className?: string
  speed?: number
  delay?: number
  cursor?: boolean
}

export function Typewriter({ text, className, speed = 50, delay = 0, cursor = true }: TypewriterProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  return (
    <span ref={ref} className={cn('inline-block', className)}>
      {isInView && (
        <>
          <motion.span>
            {text.split('').map((char, index) => (
              <motion.span
                key={`char-${index}-${char}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: 0.05,
                  delay: delay + index * (speed / 1000),
                }}
              >
                {char}
              </motion.span>
            ))}
          </motion.span>
          {cursor && (
            <motion.span
              className="ml-1"
              animate={{ opacity: [0, 1] }}
              transition={{
                duration: 0.8,
                repeat: Infinity,
                repeatType: 'reverse',
              }}
            >
              |
            </motion.span>
          )}
        </>
      )}
    </span>
  )
}

interface NumberAnimateProps {
  value: number
  className?: string
  duration?: number
  delay?: number
  prefix?: string
  suffix?: string
}

export function NumberAnimate({
  value,
  className,
  duration = 2,
  delay = 0,
  prefix = '',
  suffix = '',
}: NumberAnimateProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  return (
    <span ref={ref} className={className}>
      {prefix}
      {isInView && (
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay }}>
          <motion.span
            initial={{ '--num': 0 } as any}
            animate={{ '--num': value } as any}
            transition={{
              duration,
              delay,
              ease: 'easeOut',
            }}
            style={{
              counterReset: 'num var(--num)',
              animation: 'counter 2s ease-out forwards',
            }}
            className="tabular-nums"
          >
            <motion.span
              className="inline-block"
              style={{
                counterIncrement: 'num',
                content: 'counter(num)',
              }}
            >
              {Math.round(value)}
            </motion.span>
          </motion.span>
        </motion.span>
      )}
      {suffix}
    </span>
  )
}
